// frontend/src/components/portfolio/PublishModal.tsx
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert
} from '@mui/material';
import { Publish as PublishIcon } from '@mui/icons-material';

interface PublishModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (notes?: string) => Promise<void>;
  portfolioTitle: string;
  slug: string;
}

const PublishModal: React.FC<PublishModalProps> = ({
  open,
  onClose,
  onConfirm,
  portfolioTitle,
  slug
}) => {
  const [notes, setNotes] = useState('');
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handlePublish = async () => {
    try {
      setPublishing(true);
      setError(null);
      await onConfirm(notes);
      setNotes('');
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to publish portfolio');
    } finally {
      setPublishing(false);
    }
  };

  const handleClose = () => {
    if (!publishing) {
      setNotes('');
      setError(null);
      onClose();
    }
  };

  const publicUrl = `${window.location.origin}/portfolio/${slug}`;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PublishIcon color="primary" />
          Publish Portfolio
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" gutterBottom>
            You're about to publish <strong>{portfolioTitle}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This will make it publicly accessible at:
          </Typography>
          <Box
            sx={{
              mt: 1,
              p: 1.5,
              bgcolor: '#1f1e1eff',
              borderRadius: 1,
              fontFamily: 'monospace',
              fontSize: '0.875rem',
              wordBreak: 'break-all'
            }}
          >
            {publicUrl}
          </Box>
        </Box>

        <TextField
          fullWidth
          multiline
          rows={3}
          label="Notes (optional)"
          placeholder="Add notes about this version..."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          disabled={publishing}
          helperText="These notes are for internal tracking only"
        />

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={publishing}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handlePublish}
          disabled={publishing}
          startIcon={<PublishIcon />}
        >
          {publishing ? 'Publishing...' : 'Publish'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PublishModal;